import {
  FocusContext,
  doesFocusableExist,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { assetDetailsByIdAtom } from "@sunrise/asset";
import { selectIsDialogOpen } from "@sunrise/dialogs";
import { currentLanguageAtom, getLocalizedValue } from "@sunrise/i18n";
import { actionLocationNavigate, locationAtom } from "@sunrise/location";
import { useRecordSeries } from "@sunrise/yallo-recordings";
import { useAtomValue, useSetAtom } from "jotai";
import { ReactNode, Suspense, useEffect, useMemo } from "react";

import { Header, PageSpinner } from "@/components";
import { globalFocusKey } from "@/config/focus-key";
import { route, routeParam } from "@/config/route";
import { DetailsInfo } from "@/features/details/details-info/details-info";
import { DetailsTags } from "@/features/details/details-tags/details-tags";
import { useMenu } from "@/features/menu/use-menu";
import { RecordingsSeriesList } from "@/features/recordings/recordings-series-list";
import { useRoutes } from "@/features/routing/use-routes";
import { getAssetTags } from "@/modules/details/helpers/get-details-for-epg-and-asset";
import { useKeyboardNavigation } from "@/modules/keyboard-navigation";
import { canPullFocusAtom } from "@/modules/ui/can-pull-focus.atom";

import type { SeriesAsset } from "@sunrise/backend-types";
import type { RecordingGroupId } from "@sunrise/backend-types-core";
import { DetailsSeriesActions } from "./details-series-actions";
import { DetailsSeriesNetworkIndicator } from "./details-series-network-indicator";
import { main } from "./details-series.css";

type DetailsSeriesPageProps = {
  params: typeof routeParam.detailsSeries;
};

const localWidgetFocusKey = {
  action: (idx: number) => `${globalFocusKey.activePage}.action.${idx}`,
  list: `${globalFocusKey.activePage}.details-series.list`,
};

export function DetailsSeries({ params }: DetailsSeriesPageProps): ReactNode {
  const { assetId, recordingGroupId } = params;
  const asset = useAtomValue(assetDetailsByIdAtom(assetId));

  // It's forbidden to show menu on this page
  useMenu({ hidden: true });

  const routes = useRoutes();

  const handleOnBack = (): void => {
    routes.back();
  };

  const isDialogOpen = useAtomValue(selectIsDialogOpen);
  useKeyboardNavigation({
    onBack: handleOnBack,
    isEnabled: !isDialogOpen,
  });

  const assetData = asset.data;
  if (!assetData || assetData.type !== "series") {
    return null;
  }

  return (
    <Suspense fallback={<PageSpinner data-testid="details-series" />}>
      <DisplayDetailsSeries
        asset={assetData}
        recordingGroupId={recordingGroupId}
      />
    </Suspense>
  );
}

const TEST_ID = "details-series-page";
type DisplayDetailsSeriesProps = {
  recordingGroupId: RecordingGroupId;
  asset: SeriesAsset;
};

function DisplayDetailsSeries({
  asset,
  recordingGroupId,
}: DisplayDetailsSeriesProps): ReactNode {
  const language = useAtomValue(currentLanguageAtom);
  const tags = useMemo(() => getAssetTags(asset, language), [asset, language]);

  const { focusKey, ref, focused, focusSelf } = useFocusable({
    isFocusBoundary: true,
    autoRestoreFocus: true,
    focusKey: globalFocusKey.activePage,
    trackChildren: true,
    preferredChildFocusKey: localWidgetFocusKey.list,
  });

  const cancelButtonIndex = 3;
  const seasonButtonFocusKey = localWidgetFocusKey.action(0);
  const deleteButtonFocusKey = localWidgetFocusKey.action(4);
  const sortButtonFocusKey = localWidgetFocusKey.action(5);
  const cancelButtonFocusKey = localWidgetFocusKey.action(cancelButtonIndex);
  const dispatchLocation = useSetAtom(locationAtom);
  const onDeleteConfirm = (): void => {
    dispatchLocation(actionLocationNavigate(route.recordings.root()));
  };

  const canPullFocus = useAtomValue(canPullFocusAtom);
  useEffect(() => {
    if (canPullFocus) {
      focusSelf();
    }
  }, [focusSelf, canPullFocus]);

  const {
    onCancelRecordings,
    haveRecordingSchedules,
    onDeleteRecordings,
    haveRecordings,
  } = useRecordSeries({
    recordingGroupId,
    assetId: asset.id,
    deleteButtonFocusKey,
    onDeleteConfirm,
  });

  const cancelRecordingsAndFocusList = (): void => {
    onCancelRecordings();
    setFocus(deleteButtonFocusKey);
  };
  const focusActions = (column: number): void => {
    const actionsLength = 4; // how many divisions we have in the actions list (0 based)
    const listLength = 3; // same for the list (0 based)
    const listPercent = column / listLength;
    let columnToFocus = Math.round(listPercent * actionsLength); // closest item in the actions based on the list index
    let step = 1;

    // if that index is not available, we will try to find the closest one
    if (!doesFocusableExist(localWidgetFocusKey.action(columnToFocus))) {
      while (step < actionsLength) {
        const next = columnToFocus + step;
        const previous = columnToFocus - step;
        if (doesFocusableExist(localWidgetFocusKey.action(next))) {
          columnToFocus = next;
          break;
        }

        if (doesFocusableExist(localWidgetFocusKey.action(previous))) {
          columnToFocus = previous;
          break;
        }

        step++;
      }
    }

    // special case where the cancel button is disabled, 'dosFocusableExist' doesn't consider this case
    if (columnToFocus === cancelButtonIndex && !haveRecordingSchedules) {
      setFocus(deleteButtonFocusKey);
    } else {
      setFocus(localWidgetFocusKey.action(columnToFocus));
    }
  };

  useEffect(() => {
    if (
      !focused ||
      ((haveRecordings || haveRecordingSchedules) &&
        !doesFocusableExist(localWidgetFocusKey.list))
    ) {
      focusSelf();
    }
  }, [focused, focusSelf, haveRecordings, haveRecordingSchedules]);

  return (
    <FocusContext.Provider value={focusKey}>
      <main className={main} ref={ref} data-testid={TEST_ID}>
        <Header
          left={
            <>
              <DetailsTags
                assetId={asset.id}
                tags={tags}
                data-testid={`${TEST_ID}.details-tags`}
              />{" "}
              <DetailsSeriesNetworkIndicator
                recordingGroupId={recordingGroupId}
                seriesAssetId={asset.id}
              />
            </>
          }
        />
        <DetailsInfo
          title={getLocalizedValue(asset.title, language)}
          description={
            asset.plot ? getLocalizedValue(asset.plot, language) : null
          }
          assetId={asset.id}
          data-testid={`${TEST_ID}.details-info`}
        />
        <DetailsSeriesActions
          recordingGroupId={recordingGroupId}
          seriesAssetId={asset.id}
          cancelButtonFocusKey={cancelButtonFocusKey}
          deleteButtonFocusKey={deleteButtonFocusKey}
          seasonButtonFocusKey={seasonButtonFocusKey}
          sortButtonFocusKey={sortButtonFocusKey}
          onCancelRecordings={cancelRecordingsAndFocusList}
          onDeleteRecordings={onDeleteRecordings}
          haveRecordingSchedules={haveRecordingSchedules}
          haveRecordings={haveRecordings}
        />
        <RecordingsSeriesList
          seriesAssetId={asset.id}
          focusKey={localWidgetFocusKey.list}
          recordingGroupId={recordingGroupId}
          onExitUp={focusActions}
        />
      </main>
    </FocusContext.Provider>
  );
}
